export type OAuthProvider = 'google' | 'facebook';
export type ProfileId = string;
export type DealId = string;
export type EntityId = DealId;
export type ReportTargetId = ProfileId | number;
export type MessageReceiverId = ProfileId | number;

export interface UserData {
    id: ProfileId;
    profileType: string | null;
    [key: string]: any;
}

export interface CustomGoogleLoginResponseDto {
    profileType: string;
    isNewUser: boolean;
    [key: string]: any;
}

export interface YoutubeLinkDetails {
    ytLink: string;
    shortTitle: string;
    shortDescription: string;
}

export interface YoutubeLinkDetail {
    ytLink: string;
    shortTitle: string;
    shortDescription: string;
}

export interface SocialMediaLink {
    type: string;
    url: string;
}

export interface AchievementDetails {
    text: string;
}

export interface FightHistory {
    opponentName: string;
    date: string;
    location: string;
    result: string;
    round: number;
    typeOfEnding: string;
    organizationName?: string | undefined;
}

export interface FightHistoryDto {
    opponentName: string;
    date: string;
    location: string;
    result: string;
    round: number;
    typeOfEnding: string;
    organizationName?: string;
    preferredRuleSet?: string;
}

export interface RegisterDto {
    email: string;
    password?: string | undefined;
    phone: string | null;
    region?: string;
    bio?: string;
    profileType: 'MANAGER' | 'FIGHTER' | 'ORGANIZATION';
    profileImageUrl?: string;
    country?: string;
    provider?: string;
    nationality?: string;
    gender?: string;
    fighterDto?: {
        firstName: string;
        lastName: string;
        nickname?: string;
        country: string;
        stance: string;
        team?: string;
        gym?: string;
        socialMediaLinks?: SocialMediaLink[];
        fightStyleDescription: string;
        achievementDetails?: AchievementDetails[];
        isTitleHolder?: boolean;
        height: number;
        heightUnit: string;
        weight: number;
        weightUnit: string;
        reach: number;
        reachUnit: string;
        dateOfBirth: string;
        trainingDisciplines: string[];
        weightClasses: string[];
        ytLinkDetailsDto?: YoutubeLinkDetails[];
        fightHistoryDetailsDto?: FightHistory[];
        fighterSponsorsRequestDtos?: fighterSponsorsRequestDtos[];
    };
    managerDto?: {
        firstName: string;
        lastName: string;
        type?: string;
        organizationName?: string;
        socialMediaLinks?: SocialMediaLink[];

    };
}
export interface SocialMediaLink {
    type: string;
    url: string;
}

export interface LoginDto {
    username: string | null;
    password: string | null;
}

export interface RegionResponse {
    region: string;
    subregion: string;
}

export interface LoginResponse {
    id: ProfileId;
    username: string;
    firstName: string;
    lastName: string;
    profileType: string;
    bio: string;
    country: string;
    region: string;
    status: string;
    token: string;
    preferredLanguage: string;
}

export interface CustomPrefilledResponseDto {
    email: string;
    phone?: string;
    website?: string;
    country?: string;
    profileImage?: string;
    bio?: string;
    firstName?: string;
    lastName?: string;
    password?: string;
}

export interface FormData {
    profileType?: 'MANAGER' | 'FIGHTER' | 'ORGANIZATION';
    email?: string;
    phone?: string;
    password?: string;
    region?: string;
    bio?: string;
    profileImageUrl?: string;
    country?: string;
    provider?: string;
    profileImageFile?: File;
    videoFile?: File;
    nationality?: string;
    fighterDto?: {
        firstName?: string;
        lastName?: string;
        nickname?: string;
        country?: string;
        stance?: string;
        team?: string;
        gym?: string;
        socialMediaLinks?: SocialMediaLink[];
        fightStyleDescription?: string;
        achievementDetails?: AchievementDetails[];
        isTitleHolder?: boolean;
        height?: number;
        heightUnit?: string;
        weight?: number;
        weightUnit?: string;
        reach?: number;
        reachUnit?: string;
        dateOfBirth?: string;
        trainingDisciplines?: string[];
        weightClasses?: string[];
        ytLinkDetailsDto?: YoutubeLinkDetails[];
        fightHistoryDetailsDto?: FightHistory[];
        fighterSponsorsRequestDtos?: fighterSponsorsRequestDtos[];
    };
    managerDto?: {
        firstName?: string;
        lastName?: string;
        type?: string;
        organizationName?: string;
        socialMediaLinks?: SocialMediaLink[];

    };
    [key: string]: any;
}

export interface State {
    loading: boolean;
    userData: UserData | null;
    error: boolean;
    stage:
    | 'idle'
    | 'loading'
    | 'roleSelection'
    | 'personalDetails'
    | 'locationDetails'
    | 'contactDetails'
    | 'profileImageUpload'
    | 'fighterFormBasic'
    | 'fighterFormDetails'
    | 'fightHistory'
    | 'managerForm'
    | 'videoUpload'
    | 'navigate'
    | 'error'
    | "sponsorStage";
    formData: FormData;
    canGoBack: boolean;
    previousStage?: State['stage'];
}

export interface LoginFormData {
    email: string;
    password: string;
}

export interface RegisterFormData {
    email: string;
    password: string;
    phone: string;
}

export interface Manager {
    id: string;
    profile: {
        firstName: string;
        lastName: string;
        phone: string;
        region: string;
        residenceCountry: string;
        profileType: string;
        profileStatus: string;
    };
    type: string;
    organizationName: string;
}

export interface Fighter {
    id?: string;
    nickname?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    gender?: string;
    phoneNumber?: string;
    email?: string;
    profileImage?: string | null;
    dateOfBirth?: string;
    heightUnit?: string;
    height?: number;
    weightUnit?: string;
    weight?: number;
    reachUnit?: string;
    reach?: number;
    socialMediaLinks?: Array<{
        id: string;
        socialMediaAccountType: string;
        socialMediaAccountUrl: string;
    }>;
    trainingDisciplines?: string[];
    weightClasses?: string[];
    stance?: string;
    team?: string;
    gym?: string;
    status?: string;
    fightStyleDescription?: string;
    achievementDetails?: Array<{
        id: string;
        text: string;
    }>;
    videoUrl?: string | null;
    ytLinksDetails?: any[];
    fightHistories?: any[];
    titleHolder?: boolean;
}


export interface ReadyRosterCardDto {
    id?: string | number;
    readyRosterCardId?: string | any;
    fighter?: Fighter;
    firstName?: string;
    lastName?: string;
    purse: string;
    currency: string;
    sport: string;
    preferredRuleSet: string;
    location: string;
    availableRegions: string[];
    availableDays: string[];
    validUpto: any;
    notes: string;
    weightClass: string;
    travelReady?: boolean;
    noticePeriodDays: number;
    createdAt?: string;
    modifiedAt?: string;
    status?: string;
    rosterStatus?: string;
    contacted?: boolean;
    abuseReportResponseDtos?: AbuseReportResponseDto[];
    reported?: boolean;
    viewCount?: number;
}


export interface AbuseReportResponseDto {
    id: number;
    type: string;
    reason: string;
    reportedAt: string; // LocalDateTime in Java → ISO string in TS
    reportedBy: ProfileDto;
    reportedTo: ProfileDto;
}

export interface FighterFilterParams {
    name?: string;
    location?: string;
    trainingDisciplines?: string[];
    weightClasses?: string[];
    titleHolder?: boolean;
    wins?: number;
    fights?: number;
}

export type OpportunityCardDto = {
    id?: any,
    managerId: number;
    purse: string;
    currency: string;
    sport: string;
    preferredRuleset: string;
    weightClass: string;
    location: string;
    notes: string;
    rounds: number;
    additionalDescription: string;
    eventDate: string;
    titleShot: boolean;
    medicalSupportProvided: boolean;
    formData?: any;
};


export type OpportunityCardRequestDto = {
    templateId: string;
    templateJson: any;
    formData: Record<string, any>;
    matchupId?: number | null;
    visibility?: "MARKETPLACE" | "DIRECT_TO_FIGHTER";
};

export type OpportunityFormData = {
    id?: any;
    managerId: number;
    purse: string;
    currency: string;
    sport: string;
    preferredRuleset: string;
    weightClass: string;
    location: string;
    notes: string;
    rounds: number;
    additionalDescription: string;
    eventDate: string;
    titleShot: boolean;
    medicalSupportProvided: boolean;
};

export interface OpportunityCard {
    id: string;
    name: string;
    firstname: string;
    manager: any;
    createdBy: string;
    createdAt: string;
    updatedAt?: string;
    purse: string;
    dealType: string;
    message: string;
    currency: string;
    sport: string;
    preferredRuleset: string;
    location: string;
    notes: string;
    weightClass: string;
    rounds: number;
    medicalSupportProvided: boolean;
    titleShot: boolean;
    additionalDescription?: string;
    eventDate?: string;
    status?: string;
    opportunityType?: string;
    contacted?: boolean;
    abuseReportResponseDtos?: AbuseReportResponseDto[];
    reported?: boolean;
    price?: string;
}

export interface MessageDto {
    message: string;
    createdAt?: string;
}

export interface OpportunityCardFilterParams {
    name?: string;
    location?: string;
    combatSport?: string;
    weightClass?: string;
    opportunityType?: string;
    purse?: string;
    titleShot?: boolean;
    myCard?: boolean;
    isDeal?: boolean;
    page?: number;
    size?: number;
}

export interface UpdateOpportunityCardDto {
    marketplaceCardId: string | number;
    templateJson: any;
    formData: any;
}

export interface UpdateOpportunityCardApiDto {
    marketplaceCardId: string | number;
    templateJson: any;
    formData: any;
}

export interface ReadyRosterCard {
    id: number | string;
    fighter: Fighter;
}

export interface FieldToValidate {
    name: string;
    dtoType?: 'fighterDto' | 'managerDto';
}

export interface ViewProfileProps {
    member: Manager | Fighter | null;
    isOpen: boolean;
    onClose: () => void;
}

export interface ProfileSetting {
    id: number;
    notificationSetting: NotificationSetting;
    activationSetting?: ActivationSetting;
}

export interface ActivationSetting {
    activationRequired: boolean;
}

export interface NotificationEventPreference {
    id: number;
    name: string;
    emailNotify: boolean;
    pushNotify: boolean;
    createdAt: string;
    modifiedAt: string;
}

export interface NotificationSetting {
    id: number;
    countries: string[];
    regions: string[];
    events: NotificationEventPreference[];
    createdAt: string;
    modifiedAt: string;
}

export interface FighterStats {
    wins: number;
    ko: number;
    tko: number;
    submission: number;
    decision: number;
    dq: number;
    losses: number;
    draw: number;
    noContest: number;
    totalFights: number;
}

export interface FighterScoreResponseDto {
    age: number;
    height?: number;
    weight?: number;
    heightUnit?: "cm" | "inch" | "ft";
    weightUnit?: "kgs" | "lbs";
    amateur: FighterStats;
    professional: FighterStats;
}

export interface Profile {
    id: ProfileId;
    profileType?: 'FIGHTER' | 'MANAGER' | 'ORGANIZATION' | string;
    provider: string;
    profileStatus: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'DEACTIVATED';
    verifiedAt: string | null;
    deactivatedAt: string | null;
    nationality: string;
    reported?: boolean;
    profileSetting?: ProfileSetting;
    gender?: string;
    deletionScheduledAt?: string | null;
    fighter: {
        id: number;
        nickname: string | null;
        dateOfBirth: string;
        heightUnit: 'cm' | 'ft' | 'inch';
        height: number;
        weightUnit: 'kgs' | 'lbs';
        weight: number;
        reachUnit: 'cm' | 'ft' | 'inch';
        reach: number;
        socialMediaLinks: Array<{ socialMediaAccountType: string; socialMediaAccountUrl: string }>;
        trainingDisciplines: string[] | null;
        weightClasses: string[] | null;
        stance: 'Orthodox' | 'Southpaw' | 'Switch';
        team: string | null;
        gym: string | null;
        status: 'ACTIVE' | 'INACTIVE';
        titleHolder: boolean;
        isTitleHolder?: boolean;
        fightStyleDescription: string;
        achievementDetails: Array<{ text: string }>;
        videoUrl: string | null;
        ytLinksDetails: Array<{ ytLink: string; shortTitle: string; shortDescription: string }>;
        ytLinkDetailsDto?: YoutubeLinkDetail[];
        sponsorsResponseDtos?: SponserShip[];
        fightHistories: Array<{
            id: string;
            opponentName: string;
            date: string;
            result: 'WIN' | 'LOSS' | 'DRAW';
            typeOfEnding: string | null;
            method: string;
            round: number | null;
            time: string | null;
            location: string | null;
            organizationName: string | null;
            preferredRuleSet: string | null;
        }>;
        fighterScoreResponseDto?: FighterScoreResponseDto;
    } | null;
    manager: {
        id: number;
        type: string;
        managerType: string;
        organizationName: string;
        socialMediaLinks: Array<{ socialMediaAccountType: string; socialMediaAccountUrl: string }>;
    } | null;
    suspensionReason: string | null;
    archivedReason: string | null;
    username?: string;
    firstName: string;
    lastName: string;
    phone?: string;
    region: string;
    bio: string;
    residenceCountry: string;
    profileImage: string;
    profilePicture?: string;
    createdAt: string;
    updatedAt: string;
    visible: boolean;
    verified: boolean;
    rating: number;
    youtubeLinks?: string[];
    fightHistory?: string[];
    achievements?: string[];
    socialMedia?: string[];
    certifications?: string[];
    sponsors?: string[];
    experience?: string[];
    fightHistories: FightHistoryDto[];
    fighterManagerLinkResponseDtos?: Array<{
        id: number;
        profile: {
            id: ProfileId;
            firstName: string;
            lastName: string;
            email: string;
            profileType: 'FIGHTER';
            profileImage: string;
        };
        active: boolean;
        assignedAt: string;
        removedAt: string | null;
    }>;
}


export interface SponserShip {
    id: number;
    name: string;
    logo: string;
    description: string;
    link: string;
    createdAt?: string | null;
}

export interface ProfileDto {
    id: ProfileId;
    firstName?: string;
    lastName?: string;
    email: string;
    profileImage?: string;
    profileType?: string;
    profileStatus?: string;
}

export interface FighterManagerLinkResponseDto {
    id: number;
    profile: {
        id: ProfileId;
        firstName: string;
        userName: string
        lastName: string;
        email: string;
        profileType: 'FIGHTER';
        profileImage: string;
    };

    active: boolean;
    assignedAt: string;
    removedAt: string | null;

}

export interface NotificationDto {
    id: number;
    message: string;
    time: string;
    viewStatus: boolean;
    from: ProfileDto;
}

export interface Page<T> {
    content: T[];
    totalPages: number;
    totalElements: number;
    number: number;
    size: number;
    first: boolean;
    last: boolean;
    numberOfElements: number;
    empty: boolean;
}


export interface EditStatusModalProps {
    isOpen: boolean;
    onClose: () => void;
    profile: Profile;
}


export interface UpdateUserStatus {
    profileId: string;
    profileStatus: string;
    reason?: string;
}

export interface UpdateSocialLinkDto {
    type: string;
    url: string;
}

export interface UpdateFightHistoryDto {
    opponentName: string;
    date: string;
    location: string;
    result: string;
    round: number;
    typeOfEnding: string;
    organizationName?: string;
    preferredRuleSet: string;
}

export interface UpdateFighterDto {
    firstName: string;
    lastName: string;
    nickname?: string;
    stance: string;
    team: string;
    gym: string;
    fightStyleDescription: string;
    height: number;
    heightUnit: string;
    weight: number;
    weightUnit: string;
    reach: number;
    reachUnit: string;
    dateOfBirth: string;
    isTitleHolder: boolean;
    trainingDisciplines: string[];
    weightClasses: string[];
    achievementDetails: Array<{ text: string }>;
    ytLinkDetailsDto: Array<{ ytLink: string; shortTitle: string; shortDescription: string }>;
    fighterSponsorsRequestDtos: Array<{ name: string; description: string; logo: string; link: string }>;
    socialMediaLinks: UpdateSocialLinkDto[];
    fightHistoryDetailsDto: UpdateFightHistoryDto[];
}

export interface UpdateManagerDto {
    type?: string;
    organizationName?: string;
    socialMediaLinks: UpdateSocialLinkDto[];
}

/** Body of PUT /user/profile */
export interface UpdateUserDto {
    firstName: string;
    lastName: string;
    phone: string;
    nationality: string;
    residenceCountry: string;
    bio: string;
    gender?: string;
    profileImageUrl?: string;
    videoUrl?: string;
    fighterDto?: UpdateFighterDto;
    managerDto?: UpdateManagerDto;
}


export interface MessageResponseDto {
    id: number;
    message: string;
    fileMetadataList: FileMetadataDto[];
    sender: ProfileResponse;
    receiver: ProfileResponse;
    createdAt: string;
    type: 'CONTENT' | 'CONTENT_AND_FILE' | 'FILE' | 'SYSTEM';
    chatType?: 'INDIVIDUAL' | 'GROUP';
    dealResponseDto?: DealResponseDto;

}

export interface ProfileResponse {
    id: ProfileId;
    firstName: string;
    lastName: string;
    profileType: string;
    email: string;
    profileImage?: string;
    verified?: Boolean;
}

export interface ChatRequestDto {
    message: string;
    fileMetadataList?: FileMetadataDto[];
    type: 'CONTENT' | 'CONTENT_AND_FILE' | 'FILE';
    chatType: 'INDIVIDUAL' | 'GROUP' | 'INVITE';
    receiverId?: MessageReceiverId;
    dealId?: string;
    dealResponseDto?: DealResponseDto;
}

export interface FileMetadataDto {
    id?: number;
    fileName: string;
    fileUrl: string;
    fileType: string;
    fileSize: number;
}

export interface FileMetadata {
    fileName: string;
    fileUrl: string;
    fileType: string;
    fileSize: number;
}

export interface ContactDetailsResponseDto {
    profileDto: ProfileDto;

    unreadMessageCount: number;
}

export interface LastMessageDetail {
    message: string;
    profileDto: ProfileDto;
    createdAt: string;
}


export interface ChatMessage {
    id: string;
    sender: Profile;
    receiver: Profile;
    content: string;
    createdAt: string;
    status: boolean;
}

export interface ContactsResponseDto {
    contactDetailsResponseDtos: ContactDetails[];
    contactedProfilesCount: number;
}

export interface ContactDetails {
    profileDto: ProfileDto;
    status: string;
    lastMessageDetail: LastMessageDetail;
    unreadMessageCount: number;
}

export interface ChatRoomMember {
    id: ProfileId;
    firstName: string;
    lastName: string;
    email: string;
    profileImage?: string;
}


export interface CreateChatRoomRequest {
    name: string;
    bio?: string;
    profileImage?: string;
    memberIds: ProfileId[];
}

export interface ChatRoomsResponseDto {
    contactedProfilesCount: number;
    chatRoomResponseDtos: ChatRoom[];
}

export interface ChatRoom {
    id: number | any;
    name: string;
    bio: string;
    profileImage: string;
    createdAt: string;
    modifiedAt: string;
    createdBy: ProfileDto;
    members: ProfileDto[];
    unreadMessages: number;
    lastMessageDetail: LastMessageDetail;
}

export interface FileMetadataDto {
    id?: number;
    fileName: string;
    fileUrl: string;
    fileType: string;
    fileSize: number;
}

export interface DetailedAbuseReportResponseDto {
    id: number;
    type: string;
    reason: string;
    reportedAt: string;
    reportedBy: ProfileDto;
    reportedTo: ProfileDto;
    marketplaceCardResponseDto?: MarketplaceCardResponseDto;
    postResponseDto?: PostResponseDto;
}

export interface CreateAbuseReportRequest {
    reportedTo: ReportTargetId;
    reason: string;
    type: 'PROFILE' | 'READYROSTERCARD' | 'OPPORTUNITYCARD' | 'POST' | "MARKETPLACECARDS";
    cardId?: string;
}

export interface fighterSponsorsRequestDtos {
    name?: string;
    logo?: string;
    link?: string;
    description?: string;

}

export interface DetailedContactInviteResponseDto {
    id: number;
    contactedAt: string;
    contacted: ProfileDto;
    contactedBy: ProfileDto;
    readyRosterCardResponseDto: ReadyRosterCardDto;
    opportunityCardResponseDto: OpportunityCardDto;
    status: string;
}

export interface ContactInvitesResponseDto {
    contactedProfilesCount: number;
    inviteResponseDtos: DetailedContactInviteResponseDto[]
}


export interface ProfileDto {
    id: ProfileId;
    firstName?: string;
    lastName?: string;
    email: string;
    profileImage?: string;
    verified?: Boolean;
}

export interface Member {
    id: ProfileId;
    profileDto: ProfileDto;
}

export interface ChatGroup {
    id: number;
    name: string;
}
export interface AttachmentDto {
    fileName: string;
    fileSize: number;
    fileType: string;
    fileUrl?: string;
}

export interface DealsProposalsRequestDto {
    contactId: ProfileId;
    marketplaceCardId: string | number;

    dealType: "FIGHT_TOURNAMENT" | "SPARRING" | "FIGHT_SINGLE";

    chatType: "INDIVIDUAL" | "GROUP";
    chatRoomId?: number | null;

    formData: Record<string, any>;

    componentPayments?: {
        paymentTiming: "pre_event" | "post_event";
        dueOn: string;
    }[];

    fileMetadataList?: AttachmentDto[];

    paymentType?: PaymentType;

    signatureImage?: string;
}

export enum PaymentType {
    UPFRONT = "UPFRONT",
    SPLIT = "SPLIT"
}


export type DealStatus =
    | "PROPOSED"
    | "ACCEPTED"
    | "CONFIRMED"
    | "WAITING_FOR_RESULT"
    | "RESULT_UPDATED"
    | "RESULT_CONFIRMED"
    | "FINISHED"
    | "FIGHTER_RATED"
    | "MANAGER_RATED"
    | "CANCELLED"
    | "DISPUTE"
    | "REJECTED"
    | "EXPIRED"
    | "CLOSED";

export interface DealResponseDto {
    id: string;
    dealId: string;

    initiatedBy: CommonProfileDto;
    acceptedBy: CommonProfileDto;

    dealType: DealType;
    status: DealStatus;

    createdAt: string;
    confirmedAt?: string;

    fileMetadataList?: FileMetadataDto[];
    agreementFile?: FileMetadataDto;

    fightResult?: string;

    activityResponse?: DealActivityResponseDto[];
    ratingResponse?: RatingResponseDto[];

    componentPayments?: DealComponentPaymentResponseDto[];

    formData?: Record<string, any>;

    startDate?: string;
    endDate?: string;

    paymentType: PaymentType;

    templateJson?: any;

    matchupId?: number;
    corner?: Corner;

    dealPolicySummaryDto?: DealPolicySummaryDto;
    dealTimelineSummary?: DealTimelineSummaryDto;

    eventResponse?: EventResponseDto;
}

export enum Corner {
    RED = "RED",
    BLUE = "BLUE",
}

export interface DocumentReviewRequestDto {
    documentType: string;
    status: "APPROVED" | "REJECTED";
    reviewComment?: string;
}

export interface DealPolicySummaryDto {
    scenarios: PolicyScenarioDto[];
    baseAmount: number;
    currency: string;
    totalDealAmount: number;
}

export interface PolicyScenarioDto {
    scenario: string;
    scenarioLabel: string;
    appliedPayoutType: string;
    payoutAmount: number;
    refundAmount: number;
}


export enum DealType {
    FIGHT_SINGLE = 'FIGHT_SINGLE',
    FIGHT_TOURNAMENT = 'FIGHT_TOURNAMENT',
    SPARRING = 'SPARRING'
}

export interface ActivityItem {
    id: number;
    performedBy?: ProfileResponse | null;
    status: string;
    message: string;
    createdAt: string;
    deadlineAt?: string | null;
    deadlineProfile?: ProfileResponse | null;
}
export interface UpdateDealsProposalsRequestDto {
    dealId: string;
    dealType: "FIGHT_TOURNAMENT" | "SPARRING" | "FIGHT_SINGLE";
    formData: any;
    paymentType?: PaymentType;
    componentPayments?: DealComponentPaymentRequestDto[];
    fileMetadataList?: AttachmentDto[];
    signatureImage?: string;
}

export interface DealComponentPaymentRequestDto {
    paymentTiming: "pre_event" | "post_event";
    dueOn: string;
}
export interface FighterManagerLinkRequestDto {
    fighterIds: ProfileId[];
}

export interface InformationRequestDto {
    ipAddress?: string;
    countryCode?: string;
    city?: string;
    browserInfo?: string;
    sourcePage?: string;
}

export interface ProfileViewRequestDto {
    viewedProfileId: ProfileId;
    informationRequestDto?: InformationRequestDto;
}

export interface InformationRequestDto {
    ipAddress?: string;
    countryCode?: string;
    city?: string;
    browserInfo?: string;
    sourcePage?: string;
}

export interface CardViewRequestDto {
    marketplaceCardId?: string | number;
    readyRosterCardId?: string | number;
    informationRequestDto?: InformationRequestDto;
}


export interface CountryView {
    country: string;
    views: number;
}

export interface TrackingResponseDto {
    totalViews: number;
    countryViews: CountryView[];
}


export interface CardResponseDto {
    totalViews: number;
    cardViews: OpportunityCardView[] | ReadyRosteredCardView[];
}

export interface OpportunityCardView {
    cardId: string;
    views: number;
    cardResponseDto: CommonCardResponseDto;
    formData?: {
        core?: {
            baseAmount?: number;
            currency?: string;
        };
        details?: {
            sport?: string;
            weightClass?: string;
        };

        baseAmount?: number;
        currency?: string;

        [key: string]: any;
    };
}

export interface ReadyRosteredCardView {
    id: number;
    views: number;
    cardResponseDto: CommonCardResponseDto;

    formData?: {
        core?: {
            baseAmount?: number;
            currency?: string;
        };
        details?: {
            sport?: string;
            weightClass?: string;
        };

        baseAmount?: number;
        currency?: string;

        [key: string]: any;
    };
}

export interface CommonCardResponseDto {
    sport: string;
    preferredRuleset: string;
    weightClass: string;
    purse: string;
    currency: string;
    location: string;
    createdAt: string;
    updatedAt: string;
}

export interface NotificationSettingRequestDto {
    countries: string[];
    regions: string[];
    profileSettingId: number;
    eventPreferences: EventPreferenceDto[];
}

export interface EventPreferenceDto {
    eventName: string;
    emailNotify: boolean;
    pushNotify: boolean;
}

export interface OverAllDealResponseDto {
    id: number;
    profile: {
        id: ProfileId;
        firstName: string;
        lastName: string;
        email: string;
        profileType: string;
        profileImage?: string;
    };
    dealType: string | null;
    createdAt: string | null;
    agreementFile?: {
        fileName: string;
        fileUrl: string;
        fileType: string;
        fileSize: number;
    };
    attachmentFiles?: {
        fileName: string;
        fileUrl: string;
        fileType: string;
        fileSize: number;
    }[];
}

export interface AddMembersInChatRoom {
    groupId: number;
    memberIds: ProfileId[];
}

export interface ValidationErrors {
    [key: string]: string | undefined;
}

export interface CreateAbuseReportRequest {
    reportedTo: ReportTargetId;
    reason: string;
    type: 'PROFILE' | 'READYROSTERCARD' | 'OPPORTUNITYCARD' | 'POST' | "MARKETPLACECARDS";
    cardId?: string;
}

export interface ReportAbusePopupProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (reportType: string, reportDescription: string) => void;
    cardId?: string;
    type: 'PROFILE' | 'READYROSTERCARD' | 'OPPORTUNITYCARD' | 'MARKETPLACECARDS';
    reportedTo: ReportTargetId;
}

export interface ViewModalProps {
    isOpen: boolean;
    onClose: () => void;
    viewCard: MarketplaceCardResponseDto | null;
    marketplaceId?: string | number;
    role: string;
    activeTab: "all" | "myCards";
    loggedInUserProfileType: string;
    loggedInUserName: string;
    currentMonth: any;
    setCurrentMonth: (date: any) => void;
    fetchMarketplaceCards?: () => void;
    selectedDates: Set<string> | any;
    isPublishing: boolean;
    confirmPublishPopup: boolean;
    setConfirmPublishPopup: (open: boolean) => void;
    confirmContactPopup: boolean;
    setConfirmContactPopup: (open: boolean) => void;
    showReportPopup: boolean;
    setShowReportPopup: (open: boolean) => void;
    handlePublish: (cardId: string) => void;
    handleCreateContact: (cardId: any, e: any) => void;
}
export interface PostEventBoutDto {
    id: number;
    boutOrder?: number;
    redCorner?: CommonProfileDto | null;
    redCornerFighterName?: string | null;
    blueCorner?: CommonProfileDto | null;
    blueCornerFighterName?: string | null;
}

export interface PostEventDto {
    id: string;
    name: string;
    description?: string | null;
    startDate?: string | null;
    venue?: string | null;
    city?: string | null;
    country?: string | null;
    totalBoutCount?: number;
    bouts?: PostEventBoutDto[];
}

export interface PostResponseDto {
    id: number;
    profile: CommonProfileDto;
    title: string;
    content: string;
    postType?: string;
    event?: PostEventDto | null;
    file?: FileMetadataDto | null;
    createdAt: string;
    updatedAt: string;
    totalComments: number;
    totalLikes: number;
    liked: boolean;
    reported: boolean;
    profileType: string;

}
export interface CommonProfileDto {
    id: ProfileId;
    firstName?: string;
    lastName?: string;
    email?: string;
    username?: string;
    profileImage?: string;
    verified?: boolean;
    profileType?: string;
}


export interface PostRequestDto {
    title: string;
    description: string;
    fileMetadataDto?: FileMetadataDto;
    eventId?:string;
}


export interface CommentRequestDto {
    postId: number;
    comment: string;
}

export interface CommentResponseDto {
    id: number;
    profile: CommonProfileDto;
    content: string;
    createdAt: string;
    updatedAt: string;
}
export interface PostLikeResponseDto {
    id: number;
    profile: CommonProfileDto;
    createdAt: string;
}

export interface EditingPost {
    postId: number;
    title: string;
    content: string;
    newFiles: File[];
    clearMedia: boolean;
    error?: string;
}

export interface MarketplaceCardResponseDto {
    id: any;
    readyRosterCard?: ReadyRosterCardDto;
    opportunityCardResponse?: OpportunityCardResponseDto;
}

export interface FighterOption {
    fighterId: ProfileId;
    fighterName: string;
    status?: string;
    profileImage?: string;
}

export interface SearchableDropdownProps {
    value: string;
    options: string[];
    placeholder: string;
    disabled?: boolean;
    loading?: boolean;
    icon: "globe" | "city";
    onChange: (val: string) => void;
    isInvalid?: boolean;
    tr: (key?: string, fallback?: string) => string;
}




export interface ReadyRosterCardResponseDto {
    id: number;
    fighter?: FighterResponseDto;
    purse?: string;
    currency?: string;
    sport?: string;
    preferredRuleSet?: string;
    location?: string;
    availableRegions?: string[];
    availableDays?: string[];
    notes?: string;
    isTravelReady?: boolean;
    weightClass?: string;
    rosterStatus?: string;
    noticePeriodDays?: number;
    createdAt?: string;
    modifiedAt?: string;
    status?: string;
    validUpto?: string;
    isContacted?: boolean;
    isReported?: boolean;
}

export interface OpportunityCardResponseDto {
    id: string;
    templateJson: any;
    formData: any;
    reported: boolean;
    contacted: boolean;
    status?: string;
    eventId?: string | null;
    eventName?: string | null;
    matchupId?: number | null;
    boutOrder?: number | null;
    visibility?: string | null;
    corner?: string | null;
    manager?: OpportunityCardResponseDtoManagerSummary;
}

export interface OpportunityCardViewModel {
    id: string;
    reported: boolean;
    contacted: boolean;
    formData: any;
    templateJson: any;
    notes?: string;
    additionalDescription?: string;
    eventDate?: string;
    status?: string;
    opportunityType?: string;
    manager?: {
        firstName: string;
        lastName: string;
        profileImage?: string;
    };
}

export interface OpportunityCardResponseDtoManagerSummary {
    id?: string;
    firstName?: string;
    lastName?: string;
    profileImage?: string;
    email?: string;
    gender?: string;
    nationality?: string;
    residenceCountry?: string;
    phoneNumber?: string;
    organizationName?: string;
    managerType?: string;
    videoUrl?: string;
    socialMediaLinks?: SocialMediaAccountDto[];
}

export interface SocialMediaAccountDto {
    id?: string;
    socialMediaAccountType?: string;
    socialMediaAccountUrl?: string;
}

export interface FighterResponseDto {
    id?: string;
    firstName?: string;
    lastName?: string;
    profileImage?: string;
}

export interface AddRatingRequestDto {
    dealId: DealId;
    rating: number;
    feedback?: string | null;
}

export interface DealModalProps {
    onClose: () => void;
    opportunityCards: MarketplaceCardResponseDto[];
    onEdit: (card: MarketplaceCardResponseDto) => void;
    onDelete?: (cardId: string) => void;
    getUserRole?: () => string;
    fighterId?: ProfileId;
    isGroupChat?: boolean;
    groupMembers?: Array<{
        id: ProfileId;
        firstName?: string;
        lastName?: string;
        email: string;
        profileImage?: string;
        profileType?: string;
    }>;
    chatRoomId?: number | null;
    editMode?: boolean;
    dealToEdit?: DealResponseDto | null;
}
export interface RatingResponseDto {
    id: number;
    rating: number;
    feedback: string;
    createdAt: string;
    profile: CommonProfileDto;
    ratedBy: CommonProfileDto;
}

export interface DealActivityResponseDto {
    id: number;
    performedBy: CommonProfileDto;
    status: DealStatus;
    message: string;
    createdAt: string;
    deadlineAt?: string;
    deadlineProfile?: CommonProfileDto;
    processed?: boolean;
}

export interface GetratingResponseDto {
    id: number;
    rating: number;
    feedback: string | null;
    createdAt: string;
    profile: CommonProfileDto;
}


export type DealComponentPaymentResponseDto = {
    id?: number;
    component: string;
    paymentTiming: string;
    amount: number;
    dueOn?: string;
    paidOn?: string;
    status?: string;
};

export interface StripeStatusDto {
    status: "NEEDS_ONBOARDING" | "ACCOUNT_ACTIVE" | "VERIFICATION_PENDING";
}

export interface PaymentResponseDto {
    deal: DealResponseDto;
    sentAmount: number;
    receivedAmount: number;
    paidAt: string;
}


export interface ClosedDealPaymentsResponse {
    totalIncome: number;
    totalExpense: number;
    totalDeals: number;
    paymentResponses: PaymentResponseDto[];
}

export interface DealDisputeRequestDto {
    dealId: DealId;
    preferredResult: string | null;
    description: string;
    evidenceFiles: FileMetadataDto[];
}

export interface OpportunityCardTemplate {
    id: string;
    templateName: string;
    templateJson: any;
    createdAt: string;
    updatedAt: string;
}

export interface MarketplaceCard {
    id: string;
    templateName?: string;
    status?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface OpportunityCardTemplateJson {
    templateId: string;
    dealType: 'fight_single' | 'fight_tournament' | 'sparring';
    layout: 'stepper';
    summary?: string[];
    contractProgress?: ContractProgressStep[];
    steps: TemplateStep[];
}

export interface ContractProgressStep {
    id: string;
    title: string;
    subtitle: string;
    icon: string;
}

export interface TemplateStep {
    stepId: string;
    title: string;
    fields: TemplateField[];
    repeatable?: boolean;
    visible?: boolean;
}

export interface TemplateField {
    type: 'select' | 'number' | 'text' | 'date' | 'checkbox' | 'textarea' | 'group';
    key: string;
    label: string;
    options?: Array<{ label: string; value: string | number }>;
    validation?: {
        required?: boolean;
        min?: number;
        maxLength?: number;
    };
    visibleWhen?: Record<string, any>;
    fields?: TemplateField[];
    placeholder?: string;
    default?: any;
}


export interface FieldOption {
    value: string;
    label: string;
    visibleWhen?: Record<string, any>;
}

export interface FieldDefinition {
    key: string;
    label?: string;
    type?: string;
    default?: any;

    options?: FieldOption[];
    optionsSource?: string;
    optionsFrom?: string;
    onChange?: Array<{ action: string; target: string }>;
    colSpan?: number;

    validation?: {
        required?: boolean;
        requiredWhen?: Record<string, any>;
        maxWhen?: { mode: string; max: number };
        maxMessage?: string;
        minResolvedAmount?: {
            amount: number;
            message?: string;
            appliesWhen?: Record<string, any>;
            resolveAs?: string;
        };
        min?: number;
        max?: number;
        maxLength?: number;
        minDaysFromNow?: number;
        minDaysFromNowMessage?: string;
        onlyToday?: boolean;
        onlyTodayMessage?: string;
        uniqueAcrossItems?: boolean;
        uniqueMessage?: string;
        beforeField?: {
            field: string;
            message?: string;
            inclusive?: boolean;
        } | string;
        maxSizeMB?: number;
        allowedTypes?: string[];
    };

    step?: string | number;
    min?: number | string;
    max?: number;
    maxLength?: number;
    minRows?: number;

    repeatable?: boolean;
    itemDefaults?: Record<string, any>;
    itemTitle?: (item: any, i: number) => string;
    fields?: FieldDefinition[];

    placeholder?: string;
    accept?: string;
    storageType?: "link" | "metadata" | string;
    visibleWhen?: Record<string, any>;
    allowManualEntry?: boolean;
    nameField?: string;
    valueField?: string;
    displayField?: string;
    storeSelectedAs?: "id" | "name";
}

export interface StepDefinition {
    stepId?: string;
    id?: string;
    title?: string;
    label?: string;
    description?: string;
    placeholder?: string;
    fields?: FieldDefinition[];
    repeatable?: boolean;
    templateItemDefaults?: Record<string, any>;
}

export interface StripeAccountStatusDto {
    status: string;
    verificationSubStatus: string | null;

    payoutsEnabled: boolean;
    chargesEnabled: boolean;
    detailsSubmitted: boolean;

    disabledReason: string | null;

    requirementsCurrentlyDue: string[] | null;
    requirementsEventuallyDue: string[] | null;
    requirementsPastDue: string[] | null;
    requirementErrors: string[] | null;

    accountType: string | null;

    bankName: string | null;
    bankAccountLast4: string | null;

    onboardingUrl: string | null;
}


export interface DocumentUploadResponseDto {
    uploaded: UploadedItem[];
    failed: string[];
}

export interface UploadedItem {
    documentType: string;
    uploadStatus: string;
    uploadedFile: string;
    deadlineDate: string;
}

export interface DocumentUploadRequestDto {
    dealId: string;
    documents: DocumentItem[];
}

export interface DocumentItem {
    documentType: string;
    fileUrl: string;
    fileName: string;
    fileType: string;
    fileSize: number;
}

export interface CreateEventRequest {
    name: string;
    description?: string;
    venue?: string;
    country?: string;
    location?: string;
    city?: string;
    startDateTime?: string;
    endDateTime?: string;
}

export interface CommonProfileDto {
    id: string;
    username?: string;
    firstName?: string;
    lastName?: string;
    profileImage?: string;
    profileType?: string;
    gender?: string;
    verified?: boolean;
}


export interface EventDetailResponse {
    id: string;
    name: string;
    description: string;
    venue: string;
    country?: string;
    location?: string;
    city?: string;
    organizer: CommonProfileDto;
    fightCardId: number | null;
    startDateTime: string | null;
    endDateTime: string | null;
    matchups: MatchupResponse[];
    createdAt: string;
    modifiedAt: string;
}

export interface EventListResponse {
    id: string;
    name: string;
    venue: string;
    country?: string;
    location?: string;
    city?: string;
    organizerId?: string;
    organizerName?: string;
    organizer?: CommonProfileDto;
    startDate: string | null;
    endDate: string | null;
    matchupCount: number;
    createdAt: string | null;
}

export interface UpdateEventRequest {
    name?: string;
    description?: string;
    venue?: string;
    country?: string;
    location?: string;
    city?: string;
    startDateTime?: string;
    endDateTime?: string;
}

export interface MessageResponse {
    message: string;
}

export enum MatchupStatus {
    DRAFT = "DRAFT",
    OPPORTUNITY_POSTED = "OPPORTUNITY_POSTED",
    RED_ACCEPTED = "RED_ACCEPTED",
    BLUE_ACCEPTED = "BLUE_ACCEPTED",
    BOTH_ACCEPTED = "BOTH_ACCEPTED",
    CONFIRMED = "CONFIRMED",
    CANCELLED = "CANCELLED",
}

export interface Matchup {
    id: string | number;
    eventId?: string;
    boutOrder: number;
    titleFight: boolean;
    redCornerFighterId?: string;
    blueCornerFighterId?: string;
    redCornerFighterName?: string;   
    blueCornerFighterName?: string; 
    redCornerFighter?: CommonProfileDto | string | null;
    blueCornerFighter?: CommonProfileDto | string | null;
    status: MatchupStatus | string;
    opportunityCardId?: string | null;
    opportunityCardStatus?: string | null;
    resultType?: string | null;
    createdAt?: string;
    redCornerDeal?: DealResponseDto | null;
    blueCornerDeal?: DealResponseDto | null;
}

export interface CreateMatchupRequest {
    boutOrder: number;
    titleFight: boolean;
    redCornerFighterId?: string;
    blueCornerFighterId?: string;
    redCornerFighterName?: string;  
  blueCornerFighterName?: string; 
}

export interface MatchupResponse {
    id: number;
    boutOrder: number;
    titleFight: boolean;
    status: MatchupStatus;
    redCornerFighter: CommonProfileDto | null;
    blueCornerFighter: CommonProfileDto | null;
    redCornerFighterName?: string | null; 
    blueCornerFighterName?: string | null; 
    opportunityCardId: string | null;
    opportunityCardStatus?: string | null;
    resultType: string | null;
    createdAt: string;
    redCornerDeal?: DealResponseDto | null;
    blueCornerDeal?: DealResponseDto | null;
}

export interface DisplayMatchup {
  id?: string | number; 
  eventId?: string;
  boutOrder?: number;
  titleFight?: boolean;

  redCornerFighter?: CommonProfileDto | string | null;
  blueCornerFighter?: CommonProfileDto | string | null;
  redCornerFighterName?: string | null;
  blueCornerFighterName?: string | null;

  fighterOne?: CommonProfileDto;
  fighterTwo?: CommonProfileDto;

  status?: MatchupStatus | string;

  redCornerContacted?: boolean;
  redCornerWaiting?: boolean;
  blueCornerContacted?: boolean;
  blueCornerWaiting?: boolean;

  opportunityCardId?: string | null;
  opportunityCardStatus?: string | null;
  resultType?: string | null;
  createdAt?: string;

  redCornerDeal?: DealResponseDto | null;
  blueCornerDeal?: DealResponseDto | null;
}

export interface UpdateMatchupRequest {
    boutOrder: number;
    titleFight?: boolean;
    redCornerFighterId?: string;
    blueCornerFighterId?: string;
    redCornerFighterName?: string;  
  blueCornerFighterName?: string;
    resultType?: string;
}

export interface UpdateBoutOrderRequest{
    boutOrder:number;
}

export interface EventGraphMoneyResponse {
    currency: string;
    projectedAmount: number | null;
    fundedAmount: number | null;
    netPaidAmount: number | null;
    pendingAmount: number | null;
    refundedAmount: number | null;
    cancelledAmount: number | null;
}

export interface EventGraphFighterMoneyResponse {
    corner: string;
    fighter: CommonProfileDto | null;
    fighterName?: string | null;
    money: EventGraphMoneyResponse | null;
}

export interface EventGraphBoutResponse {
    matchupId: number;
    boutOrder: number | null;
    boutName: string;
    redCorner: CommonProfileDto | null;
    redCornerFighterName?: string | null;
    blueCorner: CommonProfileDto | null;
    blueCornerFighterName?: string | null;
    money: EventGraphMoneyResponse | null;
    fighterMoney: EventGraphFighterMoneyResponse[];
}

export interface EventGraphDetailResponse {
    eventId: string;
    eventName: string;
    eventDate: string | null;
    moneySummary: EventGraphMoneyResponse[];
    bouts: EventGraphBoutResponse[];
}



export interface ShareEventRequest {
    hoowlrProfileIds: string[];
    nonHoowlrEmails: string[];
    message?: String;
    attachments?: ShareEventFileRequest[];
}

export interface ShareEventFileRequest {
    fileName: string;
    fileUrl: string;
    fileType: string;
    fileSize: number;
    fileBase64?: string; 
}

export interface EventResponseDto {
    eventId: string;
    eventName: string;
    description?: string;
    matchupId?: number;
    opponent?: CommonProfileDto;
}

export interface DealTimelineSummaryDto {
    proposedDate?: string;
    proposedStatus?: string;

    acceptDeadline?: string;
    acceptStatus?: string;

    confirmedDate?: string;
    confirmedStatus?: string;

    prePaymentDueDate?: string;
    prePaymentStatus?: string;

    eventDate?: string;
    eventStatus?: string;

    resultUpdateDeadline?: string;
    resultUpdateStatus?: string;

    resultConfirmedDeadline?: string;
    resultConfirmedStatus?: string;

    postPaymentDueDate?: string;
    postPaymentStatus?: string;

    ratingDeadline?: string;
    ratingStatus?: string;
}

export interface EventShareHistoryResponse {
  id: number;
  eventId: string;
  eventName: string;
  sharedById: string;
  sharedByUsername: string;
  sharedByName: string;
  message: string;
  recipients: string[];
  attachments: FileMetadataDto[];
  createdAt: string;
}

export type AgreementStatus =
  | "DRAFT"
  | "UNSIGNED"
  | "ORGANIZER_SIGNED"
  | "FIGHTER_SIGNED"
  | "FULLY_SIGNED"
  | "VOID";

export interface DealAgreementResponseDto {
  id: number;
  dealId: string;
  contractNumber: string;
  status: AgreementStatus;
  organizerSignedAt?: string;
  fighterSignedAt?: string;
  voidReason?: string;
  agreementFile?: FileMetadataDto | null;
}

export type MoneyFlowRange = 'ALL' | 'WEEK' | 'MONTH';

export interface MoneyFlowPointDto {
    date: string;
    label: string;
    income: number;
    expense: number;
    net: number;
    deals: number;
}

export interface MoneyFlowResponseDto {
    currency: string;
    totalIncome: number;
    totalExpense: number;
    points: MoneyFlowPointDto[];
}

export interface EventDashboardStatsDto {
    totalEvents: number;
    draftEvents: number;
    activeEvents: number;
    completedEvents: number;
}

export interface TotalFundsDto {
    currency: string;
    totalFunds: number;
    paid: number;
    projectCosts: number;
    refunded: number;
    remainingBalance: number;
    paidPercent: number;
    projectCostsPercent: number;
    refundedPercent: number;
    remainingPercent: number;
}

export interface DealInsightsDto {
    dealStatus: string;
    totalRefund: number;
    totalExpense: number;
    totalDeals: number;
}

export interface FighterDashboardStatsDto {
    currency: string;
    totalIncome: number;
    incomeChangePercent: number;
    incomeIncreased: boolean;
    totalExpenses: number | null;
    expensesChangePercent: number;
    expensesIncreased: boolean;
    numberOfDeals: number;
    dealsChangePercent: number;
    dealsIncreased: boolean;
}

export type DashboardPendingActionSeverity = 'INFO' | 'WARNING' | 'CRITICAL';

export interface DashboardPendingActionDto {
    type: string;
    priority: number;
    title: string;
    message: string;
    ctaText: string;
    ctaLink: string;
    count: number | null;
    severity: DashboardPendingActionSeverity;
}

export interface DashboardPendingActionsResponseDto {
    actions: DashboardPendingActionDto[];
}